import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const { login, user, logout }         = useAuth();
  const navigate                        = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }
    setLoading(true);
    const { error: loginError } = await login(email, password);
    if (loginError) {
      setLoading(false);
      setError("Invalid credentials. Please try again.");
      return;
    }

    // Re-read the session to get the profile (login resolves before fetchAndSetUser completes)
    // Poll briefly for role to resolve, then gate on system_admin
    await new Promise((r) => setTimeout(r, 1200));

    // We can't read updated user here synchronously — navigate and let AdminPortal gate itself
    navigate("/admin");
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">

          {/* Logo */}
          <div className="flex flex-col items-center gap-4">
            <Link to="/" className="relative flex items-center justify-center group">
              <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-2xl scale-125" />
              <div className="relative rounded-2xl bg-card border border-border/40 shadow-2xl p-3 backdrop-blur-sm">
                <img src="/logo.png" alt="Stratakey" className="h-16 w-auto object-contain rounded-xl" />
              </div>
            </Link>
          </div>

          {/* Admin badge */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1">
              <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-amber-600 tracking-wide uppercase">Admin Access</span>
            </div>
          </div>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Admin Sign In</CardTitle>
              <CardDescription className="text-xs">
                Restricted to system administrators only.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@example.com"
                      className="pl-9"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-9 pr-9"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</>
                    : "Sign in to Admin Portal"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground">
            Regular user?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Go to standard login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
